import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { useSEO } from "@/hooks/use-seo";
import { StartFreeButton, TourButton } from "@/components/marketing/cta";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { PulseWaveformMark, PulseExamplePlayer } from "@/components/pulse-marketing";
import { buildDailyPulseJsonLd, dailyPulseCapabilities } from "@/data/feature-catalog";
import {
  ArrowRight,
  Volume2,
  Eye,
  Brain,
  CalendarCheck,
  ClipboardList,
  Inbox,
  Star,
  TrendingUp,
  Target,
  CheckCircle,
  ChevronRight,
} from "lucide-react";

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function FeaturesDailyPulsePage() {
  // Icons pair 1:1 (by order) with the shared `dailyPulseCapabilities` titles
  // so the page render, client JSON-LD, and server prerender stay in lockstep.
  const capabilityIcons = [
    Inbox,
    CalendarCheck,
    ClipboardList,
    Target,
    Brain,
    Star,
    TrendingUp,
    Eye,
  ];
  const capabilities = dailyPulseCapabilities.map((cap, i) => ({
    ...cap,
    icon: capabilityIcons[i],
  }));

  useSEO({
    tabTitle: "Daily Pulse",
    title: "Daily Pulse — The All-Seeing Eye of PadPulse",
    description:
      "Daily Pulse reads your entire PadSplit operation every morning and tells you exactly what needs your attention — a personalized spoken briefing that catches what your team might miss.",
    path: "/features/daily-pulse",
    jsonLd: buildDailyPulseJsonLd(),
    // Daily Pulse is cut (build F8): unlinked and noindexed until the Phase 2 redirect ships.
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicHeader />

      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0d1f3c] to-[#0a1628] pt-28 md:pt-36 pb-20 md:pb-28">
        {/* Background glow orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-10 left-1/4 w-96 h-96 rounded-full bg-blue-600/10 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-brand/8 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-blue-900/20 blur-3xl" />
        </div>

        <div className="relative container mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link href="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className="text-blue-400">Daily Pulse</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl">
            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-1.5 text-sm font-medium text-blue-400 mb-6">
                <Eye size={14} />
                The All-Seeing Eye of PadPulse
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.1] tracking-tight"
                data-testid="text-daily-pulse-hero-heading"
              >
                Your operation,{" "}
                <span className="text-blue-400">summarized</span>{" "}
                every morning.
              </h1>

              <p className="text-lg md:text-xl text-blue-100/80 mb-4 max-w-xl leading-relaxed">
                Daily Pulse reads your entire PadSplit operation — messages, booking requests, maintenance, devices, reviews, pipeline — and tells you exactly what matters today.
              </p>
              <p className="text-base text-blue-100/60 mb-10 max-w-xl leading-relaxed">
                It thinks like a chief of staff who never sleeps. It catches what your team might miss. And it delivers the whole thing in a 60-second spoken briefing — so you start every day fully informed.
              </p>

            </div>

            {/* Right: animated waveform + prominent voice player directly beneath it */}
            <div className="flex flex-col items-center justify-center gap-8">
              <PulseWaveformMark className="w-full max-w-md" />
              <PulseExamplePlayer size="large" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── "What Pulse does" section ─────────────────────────────────────── */}
      <section className="bg-[#0d1f3c] py-20 md:py-28">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Pulse sees everything. Every morning.
              </h2>
              <p className="text-blue-200/60 max-w-2xl mx-auto text-lg leading-relaxed">
                It continuously analyzes your entire PadSplit operation and surfaces only what's important — prioritized, spoken aloud, and ready before you finish your coffee.
              </p>
            </div>
          </AnimatedSection>

          {/* How it works steps */}
          <AnimatedSection delay={100}>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
              {[
                {
                  step: "1",
                  title: "Reads your operation",
                  description:
                    "Every morning, Pulse scans your entire PadSplit account — messages, rooms, tasks, devices, bookings, reviews, pipeline, and financials.",
                },
                {
                  step: "2",
                  title: "Prioritizes what matters",
                  description:
                    "Gemini AI analyzes the signals and ranks what actually needs your attention today — not just a data dump, but a curated, ordered list.",
                },
                {
                  step: "3",
                  title: "Tells it to you",
                  description:
                    "A personalized spoken briefing delivered by Paul, the Pulse voice — clear, confident, and done in under 60 seconds.",
                },
              ].map((step) => (
                <div
                  key={step.step}
                  className="relative bg-white/5 border border-white/10 rounded-2xl p-6"
                  data-testid={`card-pulse-step-${step.step}`}
                >
                  <div className="w-9 h-9 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold text-sm mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Capability grid */}
          <AnimatedSection delay={150}>
            <h3 className="text-center text-xl font-semibold text-white mb-10">
              Everything Pulse monitors
            </h3>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <AnimatedSection key={cap.title} delay={i * 50}>
                  <div
                    className="bg-white/4 border border-white/8 hover:border-blue-500/30 hover:bg-white/6 rounded-xl p-5 transition-all duration-300 group"
                    data-testid={`card-pulse-cap-${i}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/25 flex items-center justify-center mb-3 group-hover:bg-blue-500/25 transition-colors">
                      <Icon size={16} className="text-blue-400" />
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-1.5">{cap.title}</h4>
                    <p className="text-white/45 text-xs leading-relaxed">{cap.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── The "all-seeing eye" positioning section ─────────────────────── */}
      <section className="bg-gray-950 py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                The chief of staff{" "}
                <span className="text-blue-400">that never sleeps.</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Most hosts run their operation reactively — responding to what comes to them. Pulse flips that. It proactively scans everything and brings only what matters to the top, every single day.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                It catches the booking request your team didn't flag. The inspection deadline that's tomorrow. The thermostat that's reading 3 degrees high. The negative review that just came in overnight. You don't have to go looking — Pulse finds it for you.
              </p>
              <ul className="space-y-3">
                {[
                  "Never miss a critical deadline again",
                  "Start every day fully informed in under 60 seconds",
                  "Catches what your team might overlook",
                  "Delivered as a spoken briefing — hands-free and natural",
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-gray-300">
                    <CheckCircle size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              {/* Quote / sample item display */}
              <div className="space-y-3">
                {[
                  {
                    priority: "high",
                    category: "Booking Requests",
                    headline: "3 booking requests today — 1 waiting 8 hours",
                    detail: "The 8-hour request has submitted their form. Review and approve or reject.",
                  },
                  {
                    priority: "high",
                    category: "Acquisitions",
                    headline: "Inspection deadline tomorrow — 123 Main St",
                    detail: "Confirm purchase or contact your realtor to cancel before end of day.",
                  },
                  {
                    priority: "medium",
                    category: "Tasks",
                    headline: "Malfunctioning door lock — vendor accepted",
                    detail: "Task accepted by vendor. Completion scheduled for tomorrow.",
                  },
                  {
                    priority: "medium",
                    category: "Smart Devices",
                    headline: "Thermostat at 506 Main St reading 3° high",
                    detail: "Set to 72°F, holding at 75°F. Dispatch a vendor to investigate?",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#0d1f3c] border border-white/8 rounded-xl p-4"
                    data-testid={`card-pulse-sample-item-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          item.priority === "high"
                            ? "bg-red-500/15 text-red-400 border border-red-500/20"
                            : "bg-amber-500/15 text-amber-400 border border-amber-500/20"
                        }`}
                      >
                        {item.priority === "high" ? "High" : "Medium"}
                      </span>
                      <span className="text-xs text-white/30">{item.category}</span>
                    </div>
                    <p className="text-white text-sm font-medium mb-1">{item.headline}</p>
                    <p className="text-white/45 text-xs leading-relaxed">{item.detail}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ─── Voice section ─────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-[#0a1628] py-20 md:py-28">
        <div className="container mx-auto px-6 text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-1.5 text-sm font-medium text-blue-400 mb-6">
              <Volume2 size={14} />
              Voice Briefings
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Hear it. Don't read it.
            </h2>
            <p className="text-blue-200/60 max-w-xl mx-auto text-lg leading-relaxed mb-10">
              Pulse speaks your briefing aloud in a clear, confident voice. Tap play on your phone while you get ready, and walk out the door informed — no screen required.
            </p>

            <PulseExamplePlayer />

            <p className="text-xs text-white/25 mt-6 max-w-sm mx-auto">
              This is a real example briefing. In the app, your briefing reflects your actual portfolio.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── See more features ──────────────────────────────────────────────── */}
      <section className="bg-gray-900 py-12">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto">
              <Link
                href="/features/messages"
                className="group flex items-center justify-between gap-4 p-5 bg-gray-950 border border-white/10 rounded-2xl hover:border-white/20 transition-colors"
                data-testid="link-daily-pulse-see-more-features"
              >
                <div>
                  <span className="text-xs text-white/30">See more features →</span>
                  <p className="text-base font-semibold text-white group-hover:opacity-90">
                    Jump back into the feature tour
                  </p>
                  <p className="text-xs text-white/40 mt-0.5">
                    Start with Messages and explore everything PadPulse does.
                  </p>
                </div>
                <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl bg-brand-accent/15 border border-brand-accent/30 group-hover:translate-x-0.5 transition-transform">
                  <ArrowRight size={18} className="text-brand-accent" />
                </span>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── CTA ────────────────────────────────────────────────────────────── */}
      <section className="bg-gray-950 py-16 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to start every day informed?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Sign up for access to Daily Pulse and the full PadPulse platform.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <StartFreeButton ctaId="daily-pulse-start-free" location="features_daily_pulse" className="px-10" />
              <TourButton ctaId="daily-pulse-tour" location="features_daily_pulse" className="px-8" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
